// ✅ FocusGame.jsx - Continuous session streak tracking + persistent best synced

import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../../firebase';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import Confetti from 'react-confetti';
import toast, { Toaster } from 'react-hot-toast';
import correctSoundFile from '../../assets/correct.mp3';
import wrongSoundFile from '../../assets/wrong.mp3';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { useWindowSize } from '@uidotdev/usehooks';
import { onAuthStateChanged } from 'firebase/auth';


const colors = ["red", "blue", "green", "black", "purple", "orange"];

export default function FocusGame() {
  // UI & game state
  const [color, setColor] = useState("red");
  const [displayWord, setDisplayWord] = useState("red");
  const [score, setScore] = useState(0);
  const [message, setMessage] = useState("");
  const [chronos, setChronos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [delay, setDelay] = useState(3000);
  const [timerPercent, setTimerPercent] = useState(100);
  const [isGameOver, setIsGameOver] = useState(false);
  const [dailyPlays, setDailyPlays] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const [maxPlays, setMaxPlays] = useState(25);
  const [comboCount, setComboCount] = useState(0); // continuous session streak
  const [showConfetti, setShowConfetti] = useState(false);
  const [floatingBonus, setFloatingBonus] = useState(null);
  const [shake, setShake] = useState(false);

  const navigate = useNavigate();
  const { width, height } = useWindowSize();
  const correctSound = useRef(new Audio(correctSoundFile));
  const wrongSound = useRef(new Audio(wrongSoundFile));

  // persist best session streak + chronos to leaderboard
  const updateLeaderboard = async (sessionStreak) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const userRef = doc(db, 'users', uid);
    const snap = await getDoc(userRef);
    const profile = snap.exists() ? snap.data() : {};
    const name = profile.name || auth.currentUser.displayName || 'Brainy Player';
    const userChronos = profile.chronos || 0;
    const lbRef = doc(db, 'leaderboard_master', uid);
    const lbSnap = await getDoc(lbRef);
    const today = new Date().toDateString();

    if (lbSnap.exists()) {
      const data = lbSnap.data();
      await updateDoc(lbRef, {
        name,
        chronos: userChronos,
        bestFocusGameStreak: Math.max(data.bestFocusGameStreak || 0, sessionStreak),
        lastPlayed: today,
        updatedAt: serverTimestamp()
      });
    } else {
      await setDoc(lbRef, {
        name,
        chronos: userChronos,
        bestFocusGameStreak: sessionStreak,
        bestNumberMemoryLevel: 0,
        bestSynonymWords: 0,
        lastPlayed: today,
        createdAt: serverTimestamp()
      });
    }
  };

// FocusGame.jsx – Guard Firestore calls until auth is ready
// FocusGame.jsx – Guard Firestore calls until auth is ready
useEffect(() => {
  // Wait for Firebase Auth to initialize and provide a user
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    if (user) {
      const init = async () => {
        const userRef = doc(db, 'users', user.uid);
        const snap = await getDoc(userRef);
        if (!snap.exists()) return;
        const data = snap.data();
        const today = new Date().toDateString();

        if (data.focusGamePlaysDate !== today) {
          await updateDoc(userRef, {
            focusGamePlays: 0,
            focusGamePlaysDate: today

          });
                                 toast.success('🌅 New day, new focus!');

        
        }

        // Only read, no writes
        const plays = (data.focusGamePlaysDate === today) ? (data.focusGamePlays || 0) : 0;
        setDailyPlays(plays);
        setChronos(data.chronos || 0);

        const limit = data.accessType === 'pro' ? 200 : 25;
        setMaxPlays(limit);
        setLimitReached(plays >= limit);

        if (plays < limit) nextWord();
        else setIsGameOver(true);

        setLoading(false);
      };
      init();
    } else {
      // No logged-in user; stop loading spinner
      setLoading(false);
    }
  });

  return () => unsubscribe();
}, []);




  // countdown
  useEffect(() => {
    if (isGameOver || limitReached) return;
    const iv = setInterval(() => {
      setTimerPercent(prev => {
        const next = Math.max(0, prev - 100 / (delay / 100));
        if (next === 0) {
          setIsGameOver(true);
          setMessage("⏰ Time's up! Game Over.");
          wrongSound.current.play();
          endPlay(0);
        }
        return next;
      });
    }, 100);
    return () => clearInterval(iv);
  }, [delay, displayWord, isGameOver, limitReached]);

  // handle play end and persist
  const endPlay = async (bonus) => {
    const userRef = doc(db, 'users', auth.currentUser.uid);
    const snap = await getDoc(userRef);
    if (!snap.exists()) return;
    const data = snap.data();
    const today = new Date().toDateString();

    if (data.focusGamePlaysDate === today && data.focusGamePlays >= maxPlays) {
      setLimitReached(true);
      setIsGameOver(true);
      return;
    }

    const plays = (data.focusGamePlays || 0) + 1;
    const newChronos = (data.chronos || 0) + bonus;
    await updateDoc(userRef, {
      chronos: newChronos,
      focusGamePlays: plays,
      focusGamePlaysDate: today,
      updatedAt: serverTimestamp()
    });
    setChronos(newChronos);
    setDailyPlays(plays);
    setLimitReached(plays >= maxPlays);
    updateLeaderboard(comboCount);
  };

  const nextWord = () => {
    setColor(colors[Math.floor(Math.random() * colors.length)]);
    setDisplayWord(colors[Math.floor(Math.random() * colors.length)]);
    setTimerPercent(100);
  };

  const getBonus = () => delay > 2500 ? 1 : delay > 2000 ? 2 : delay > 1500 ? 3 : 4;

  const handleAnswer = (sel) => {
    if (isGameOver || limitReached) return;
    if (dailyPlays >= maxPlays) {
      setLimitReached(true);
      setIsGameOver(true);
      return;
    }
    if (sel === color) {
      correctSound.current.play();
      const bonus = getBonus();
      setScore(s => s + bonus);
      setMessage(`✅ Correct! +${bonus}`);
      setFloatingBonus(`+${bonus}`);
      setTimeout(() => setFloatingBonus(null), 1000);
      setComboCount(c => c + 1);
      if (comboCount + 1 === 10 || comboCount + 1 === 20) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
      if (delay > 1200) setDelay(d => d - 100);
      endPlay(bonus);
      nextWord();
    } else {
      wrongSound.current.play();
      setMessage("❌ Wrong! Game Over.");
      setShake(true);
      setTimeout(() => {
        setShake(false);
        setIsGameOver(true);
        endPlay(0);
      }, 200);
    }
  };

  const restartGame = () => {
    setIsGameOver(false);
    setScore(0);
    setDelay(3000);
    setMessage("");
    setComboCount(0);
    nextWord();
  };

  const timerBarColor = timerPercent > 60 ? 'bg-green-500' : timerPercent > 30 ? 'bg-yellow-400' : 'bg-red-500';

  return (
    <>
      <Navbar />
      <Toaster position="top-center" />
      {showConfetti && <Confetti width={width} height={height} />}
      {limitReached && <Confetti recycle={false} numberOfPieces={400} />}

      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-pink-100 to-yellow-100 p-6">
        <h1 className="text-3xl font-bold text-purple-800 mb-2">🎯 Focus Game</h1>
        <p className="text-purple-700 font-semibold mb-1">Tap the button that matches the COLOR of the word below!</p>

        {limitReached ? (
          <div className="text-center text-lg font-semibold text-red-600 mb-6">
            🎉 With Great Focus, Go conquer your other goals!
          </div>
        ) : (
          <>
            <div className="w-full max-w-md h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
              <div className={`${timerBarColor} h-full transition-all duration-100`} style={{ width: `${timerPercent}%` }} />
            </div>

            <div className="relative mb-6">
              {floatingBonus && <div className="floating-bonus">{floatingBonus}</div>}
              <div className={`text-4xl font-bold mb-6 ${shake ? 'shake' : ''}`} style={{ color }}>
                {displayWord}
              </div>
            </div>

            <div className="mb-4 text-lg text-gray-800">Score: <span className="font-bold">{score}</span></div>
            {!limitReached && !loading && <div className="mb-2 text-purple-700 font-semibold">Chronos: {chronos} ⏳</div>}
            <div className="mb-2 text-orange-600 font-semibold">🔥 Streak: {comboCount}</div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {colors.map(c => (
                <button
                  key={c}
                  onClick={() => handleAnswer(c)}
                  disabled={isGameOver}
                  className="px-6 py-3 rounded-lg font-bold shadow-md hover:scale-105 transform" 
                  style={{ color: c, border: `2px solid ${c}`, backgroundColor: 'white' }}
                >{c.toUpperCase()}</button>
              ))}
            </div>

            <div className="text-xl font-semibold text-purple-600 min-h-[2rem]">{message}</div>

            {isGameOver && !limitReached && (
              <button onClick={restartGame} className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700">
              🔁 Play Again
            </button>
          )}

          {isGameOver && limitReached && (
            <button
              onClick={() => toast('✅ Amazing focus today. Come back tomorrow and keep sharpening your mind!')}
              className="mt-6 px-6 py-3 bg-purple-400 text-white rounded-lg font-bold hover:bg-purple-500"
            >
              🔁 Come Back Tomorrow
            </button>
            )}
          </>
        )}

        <style jsx>{`
          @keyframes floatUp {0% {opacity:1; transform:translateY(0);}100% {opacity:0; transform:translateY(-40px);}}
          .floating-bonus {position:absolute; top:-1.5rem; left:50%; transform:translateX(-50%); font-size:2rem; font-weight:bold; color:#4caf50; animation:floatUp 1s ease forwards;}
          @keyframes shake {0%{transform:translateX(0);}25%{transform:translateX(-10px);}50%{transform:translateX(8px);}75%{transform:translateX(-10px);}100%{transform:translateX(0);}}
          .shake {animation:shake 0.2s;}
        `}</style>
      </div>
    </>
  );
}
